import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  KeyRound, UserPlus, Trash2, ShieldCheck, Users, Loader2, LockKeyhole,
} from 'lucide-react';
import { authApi, adminApi } from '../../utils/api';

const inputCls =
  'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson tracking-widest';
const inputPlainCls =
  'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';
const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft';

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white border border-line rounded-[14px] p-5 md:p-6">
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-line">
        <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <Icon size={19} />
        </span>
        <div>
          <h3 className="font-display text-[17px] font-bold text-ink leading-tight">{title}</h3>
          <p className="text-[12px] text-muted">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function AdminSettings() {
  // ---- STEP 1 popup modal gate password change ----
  const [modalPwForm, setModalPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [modalPwLoading, setModalPwLoading] = useState(false);

  // ---- Admin account password change ----
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const updatePwField = (key, val) => setPwForm((f) => ({ ...f, [key]: val }));

  // ---- New admin credentials ----
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
  const [createLoading, setCreateLoading] = useState(false);

  // ---- Existing admins list ----
  const [admins, setAdmins] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [meId, setMeId] = useState(null);

  const fetchAdmins = async () => {
    setListLoading(true);
    try {
      const [listRes, meRes] = await Promise.all([adminApi.getAll(), authApi.me().catch(() => null)]);
      setAdmins(Array.isArray(listRes.data) ? listRes.data : []);
      if (meRes?.data?.id) setMeId(meRes.data.id);
    } catch (err) {
      console.error('Error fetching admins:', err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // ---- Modal (popup gate) password change ----
  const submitModalPassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = modalPwForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('सबै फिल्ड भर्नुहोस्।');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('नयाँ पासवर्ड कम्तीमा ६ अक्षरको हुनुपर्छ।');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('नयाँ पासवर्ड र पुष्टि मिलेन।');
      return;
    }
    setModalPwLoading(true);
    try {
      await authApi.changeModalPassword(currentPassword, newPassword);
      toast.success('मोडल पासवर्ड परिवर्तन भयो।');
      setModalPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'पासवर्ड परिवर्तन असफल भयो।');
    } finally {
      setModalPwLoading(false);
    }
  };

  // ---- Admin account password change ----
  const submitChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = pwForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('सबै फिल्ड भर्नुहोस्।');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('नयाँ पासवर्ड कम्तीमा ६ अक्षरको हुनुपर्छ।');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('नयाँ पासवर्ड र पुष्टि मिलेन।');
      return;
    }
    setPwLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('पासवर्ड परिवर्तन भयो।');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'पासवर्ड परिवर्तन असफल भयो।');
    } finally {
      setPwLoading(false);
    }
  };

  // ---- Add new admin ----
  const submitCreateAdmin = async () => {
    const { name, email, password } = newAdmin;
    if (!name.trim() || !email.trim() || !password) {
      toast.error('सबै फिल्ड भर्नुहोस्।');
      return;
    }
    if (password.length < 6) {
      toast.error('पासवर्ड कम्तीमा ६ अक्षरको हुनुपर्छ।');
      return;
    }
    if (password === modalPwForm.newPassword && password) {
      toast.error('एडमिन पासवर्ड मोडल पासवर्डभन्दा फरक हुनुपर्छ।');
      return;
    }
    setCreateLoading(true);
    try {
      await adminApi.create({ name: name.trim(), email: email.trim(), password });
      toast.success('नयाँ एडमिन थपियो।');
      setNewAdmin({ name: '', email: '', password: '' });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.error || 'एडमिन थप्न असफल भयो।');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (admin) => {
    if (admin.id === meId) {
      toast.error('आफ्नै खाता मेट्न मिल्दैन।');
      return;
    }
    if (!window.confirm(`"${admin.name}" एडमिन हटाउने हो?`)) return;
    try {
      await adminApi.remove(admin.id);
      toast.success('एडमिन हटाइयो।');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.error || 'मेट्न असफल भयो।');
    }
  };

  return (
    <div className="flex flex-col gap-5 max-w-[860px]">
      <div>
        <h2 className="font-display text-[22px] font-bold text-ink">सेटिङ</h2>
        <p className="text-[13px] text-muted">लगइन पासवर्डहरू र एडमिन व्यवस्थापन।</p>
      </div>

      {/* STEP 1 — Popup modal gate password */}
      <SectionCard
        icon={LockKeyhole}
        title="मोडल पासवर्ड (पपअप)"
        subtitle="लगइन popup को पहिलो पासवर्ड — एडमिन खाताको पासवर्डभन्दा फरक हुनुपर्छ"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <label className={labelCls}>
            हालको मोडल पासवर्ड
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={modalPwForm.currentPassword}
              onChange={(e) => setModalPwForm((f) => ({ ...f, currentPassword: e.target.value }))}
              placeholder="••••••••"
            />
          </label>
          <label className={labelCls}>
            नयाँ मोडल पासवर्ड
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={modalPwForm.newPassword}
              onChange={(e) => setModalPwForm((f) => ({ ...f, newPassword: e.target.value }))}
              placeholder="••••••••"
            />
          </label>
          <label className={labelCls}>
            पुष्टि गर्नुहोस्
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={modalPwForm.confirmPassword}
              onChange={(e) => setModalPwForm((f) => ({ ...f, confirmPassword: e.target.value }))}
              placeholder="••••••••"
              onKeyDown={(e) => e.key === 'Enter' && submitModalPassword()}
            />
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={submitModalPassword}
            disabled={modalPwLoading}
            className="inline-flex items-center gap-2 bg-emerald-700 text-white font-semibold text-[13px] px-5 py-2.5 rounded-full hover:bg-emerald-800 disabled:opacity-60 transition-colors"
          >
            {modalPwLoading ? <Loader2 size={14} className="animate-spin" /> : <LockKeyhole size={14} />}
            {modalPwLoading ? 'परिवर्तन हुँदै...' : 'मोडल पासवर्ड बदल्नुहोस्'}
          </button>
        </div>
      </SectionCard>

      {/* Admin account password change */}
      <SectionCard
        icon={KeyRound}
        title="एडमिन खाताको पासवर्ड परिवर्तन"
        subtitle="तपाईंको खाता (इमेल + पासवर्ड) को पासवर्ड — मोडल पासवर्डभन्दा फरक हुनुपर्छ"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <label className={labelCls}>
            हालको पासवर्ड
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={pwForm.currentPassword}
              onChange={(e) => updatePwField('currentPassword', e.target.value)}
              placeholder="••••••••"
            />
          </label>
          <label className={labelCls}>
            नयाँ पासवर्ड
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={pwForm.newPassword}
              onChange={(e) => updatePwField('newPassword', e.target.value)}
              placeholder="••••••••"
            />
          </label>
          <label className={labelCls}>
            नयाँ पासवर्ड पुष्टि
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={pwForm.confirmPassword}
              onChange={(e) => updatePwField('confirmPassword', e.target.value)}
              placeholder="••••••••"
              onKeyDown={(e) => e.key === 'Enter' && submitChangePassword()}
            />
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={submitChangePassword}
            disabled={pwLoading}
            className="inline-flex items-center gap-2 bg-emerald-700 text-white font-semibold text-[13px] px-5 py-2.5 rounded-full hover:bg-emerald-800 disabled:opacity-60 transition-colors"
          >
            {pwLoading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {pwLoading ? 'परिवर्तन हुँदै...' : 'पासवर्ड परिवर्तन गर्नुहोस्'}
          </button>
        </div>
      </SectionCard>

      {/* Add new admin credentials */}
      <SectionCard
        icon={UserPlus}
        title="नयाँ एडमिन थप्नुहोस्"
        subtitle="नयाँ क्रेडेन्सियल सिर्जना गर्नुहोस् — पासवर्ड सुरक्षित रूपमा संगृहीत हुन्छ"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <label className={labelCls}>
            नाम
            <input
              type="text"
              className={inputPlainCls}
              value={newAdmin.name}
              onChange={(e) => setNewAdmin((f) => ({ ...f, name: e.target.value }))}
              placeholder="एडमिनको नाम"
            />
          </label>
          <label className={labelCls}>
            इमेल
            <input
              type="email"
              className={inputPlainCls}
              value={newAdmin.email}
              onChange={(e) => setNewAdmin((f) => ({ ...f, email: e.target.value }))}
              placeholder="admin@example.com"
            />
          </label>
          <label className={labelCls}>
            पासवर्ड
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={newAdmin.password}
              onChange={(e) => setNewAdmin((f) => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
              onKeyDown={(e) => e.key === 'Enter' && submitCreateAdmin()}
            />
          </label>
        </div>
        <p className="text-[11.5px] text-muted mt-2">
          नोट: यहाँको पासवर्ड मोडल पासवर्डजस्तै हुन पाउँदैन — दुवै फरक हुनुपर्छ।
        </p>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={submitCreateAdmin}
            disabled={createLoading}
            className="inline-flex items-center gap-2 bg-emerald-700 text-white font-semibold text-[13px] px-5 py-2.5 rounded-full hover:bg-emerald-800 disabled:opacity-60 transition-colors"
          >
            {createLoading ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {createLoading ? 'थप्दै...' : 'एडमिन थप्नुहोस्'}
          </button>
        </div>
      </SectionCard>

      {/* Existing admins */}
      <SectionCard
        icon={Users}
        title="एडमिनहरू"
        subtitle="प्रणालीमा रहेका सबै एडमिन खाताहरू"
      >
        {listLoading ? (
          <div className="flex items-center gap-2 text-muted text-[13px] py-3">
            <Loader2 size={15} className="animate-spin" /> लोड हुँदै...
          </div>
        ) : admins.length === 0 ? (
          <p className="text-[13px] text-muted py-2">कुनै एडमिन भेटिएन।</p>
        ) : (
          <div className="flex flex-col gap-2.5">
            {admins.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 border border-line rounded-xl px-4 py-3"
              >
                <span className="w-9 h-9 rounded-full bg-crimson text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
                  {String(a.name || 'A').trim().charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-semibold text-ink truncate">{a.name}</span>
                  <span className="block text-[12px] text-muted truncate">{a.email}</span>
                </div>
                {a.id === meId ? (
                  <span className="text-[11px] font-bold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full flex-shrink-0">
                    तपाईं
                  </span>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] text-crimson hover:bg-red-50 transition-colors flex-shrink-0"
                    onClick={() => handleDelete(a)}
                    aria-label="Delete admin"
                    title="हटाउनुहोस्"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

export default AdminSettings;
